import { z } from "zod";
import { UNIT_CODES } from "../units";
import { money, pageQuery } from "./common";

const isoDate = z.iso.date({ error: "Дата в формате ГГГГ-ММ-ДД" });
const comment = z.string().trim().max(500, { error: "Максимум 500 символов" }).nullable().optional();

// ───────────── Расходы (ТЗ 09 §9.8, FR-FIN-007) ─────────────

/** Категории расходов. Закупка продуктов — не расход, а документ «Закупка» (иначе двойной счёт). */
export const EXPENSE_CATEGORIES = ["rent", "salary", "utilities", "repair", "marketing", "transport", "household", "taxes", "other"] as const;
export type ExpenseCategory = (typeof EXPENSE_CATEGORIES)[number];

const positive = money.min(1, { error: "Сумма должна быть больше нуля" });

export const expenseSchema = z.strictObject({
  date: isoDate,
  category: z.enum(EXPENSE_CATEGORIES, { error: "Выберите категорию" }),
  amount: positive,
  comment,
});
export const expenseUpdateSchema = expenseSchema.partial().extend({ version: z.number().int().min(1) }).strict();
export type ExpenseInput = z.output<typeof expenseSchema>;

export const expensesQuery = pageQuery.extend({
  from: isoDate.optional(),
  to: isoDate.optional(),
  category: z.enum(EXPENSE_CATEGORIES).optional(),
});

// ───────────── Закупки и списания (ТЗ 08 §8.4: receipt / writeoff) ─────────────

/** receipt — закупка, writeoff — списание, return_to_supplier — возврат поставщику (BR-123: со склада FIFO, кредит к долгу). */
export const STOCK_DOC_TYPES = ["receipt", "writeoff", "return_to_supplier"] as const;
export type StockDocType = (typeof STOCK_DOC_TYPES)[number];
/** Причины списания (ТЗ 08 §98). */
export const WRITEOFF_REASONS = ["spoilage", "expired", "cooking_error", "breakage", "staff_meal", "tasting", "internal_use", "unknown"] as const;
export type WriteoffReason = (typeof WRITEOFF_REASONS)[number];

/** Потери при приёмке закупки: процент от количества или фиксированное количество в единице строки (кг, л, шт). */
export const RECEIPT_LOSS_MODES = ["percent", "qty"] as const;
export type ReceiptLossMode = (typeof RECEIPT_LOSS_MODES)[number];
export type ReceiptLoss = { mode: ReceiptLossMode; value: number };

/** Сколько потеряно при приёмке — в единице строки (`qty` и результат — в одной единице). */
export function receiptLossQty(qty: number, loss: ReceiptLoss | null | undefined): number {
  if (!loss || !(loss.value > 0) || !(qty > 0)) return 0;
  const lost = loss.mode === "percent" ? (qty * loss.value) / 100 : loss.value;
  return Math.round(Math.min(lost, qty) * 10_000) / 10_000;
}

const stockLine = z.strictObject({
  productId: z.uuid({ error: "Выберите продукт" }),
  qty: z.number({ error: "Укажите количество" }).positive({ error: "Количество должно быть больше нуля" }).max(1e9),
  unit: z.enum(UNIT_CODES as [string, ...string[]], { error: "Неизвестная единица" }),
  /** Сумма строки, minor units. Для закупки обязательна; для списания — по последней цене закупки, если не указана. */
  amount: money.nullable().optional(),
  /** Срок годности партии (закупка, addon.inventory_pro); без него — дата + срок хранения продукта */
  expiresAt: isoDate.nullable().optional(),
  /** Потери при приёмке (только закупка): на склад — количество за вычетом, сумма строки не меняется (цена за единицу растёт) */
  loss: z
    .strictObject({
      mode: z.enum(RECEIPT_LOSS_MODES),
      value: z.number({ error: "Укажите потери" }).min(0, { error: "Не меньше нуля" }).max(1e9),
    })
    .nullable()
    .optional(),
});

export const stockDocumentSchema = z
  .strictObject({
    type: z.enum(STOCK_DOC_TYPES),
    date: isoDate,
    /** Склад филиала; по умолчанию — основной (другой склад — addon.inventory_pro) */
    warehouseId: z.uuid().nullable().optional(),
    supplier: z.string().trim().max(200, { error: "Максимум 200 символов" }).nullable().optional(),
    /** Поставщик из справочника: закупка становится его счётом (долг = сумма − оплаты, BR-125) */
    supplierId: z.uuid({ error: "Выберите поставщика" }).nullable().optional(),
    /** Срок оплаты; по умолчанию — дата + отсрочка поставщика */
    dueDate: isoDate.nullable().optional(),
    /** Возврат поставщику по закупке: цены и партии — из неё, не больше закупленного (поставщик — из закупки) */
    sourceDocumentId: z.uuid({ error: "Некорректная закупка" }).nullable().optional(),
    /** Оплачено сразу целиком: cash — из смены веб-кассы, bank — переводом; null — в долг */
    paid: z.enum(["cash", "bank"]).nullable().optional(),
    reason: z.enum(WRITEOFF_REASONS, { error: "Выберите причину" }).nullable().optional(),
    comment,
    lines: z.array(stockLine).min(1, { error: "Добавьте хотя бы одну строку" }).max(200),
  })
  .refine((d) => d.type !== "writeoff" || !!d.reason, { error: "Выберите причину", path: ["reason"] })
  .refine((d) => d.type !== "receipt" || d.lines.every((l) => l.amount !== null && l.amount !== undefined), { error: "Укажите сумму", path: ["lines"] })
  .refine((d) => !d.paid || !!d.supplierId, { error: "Оплата фиксируется только для поставщика из справочника", path: ["paid"] })
  .refine((d) => d.type !== "return_to_supplier" || !!d.supplierId || !!d.sourceDocumentId, { error: "Выберите поставщика", path: ["supplierId"] })
  .refine((d) => !d.sourceDocumentId || d.type === "return_to_supplier", { error: "Закупка-источник — только для возврата", path: ["sourceDocumentId"] })
  .refine((d) => d.type === "receipt" || (!d.paid && !d.dueDate), { error: "Оплата и срок — только для закупки", path: ["paid"] })
  .refine((d) => !d.dueDate || d.dueDate >= d.date, { error: "Срок оплаты раньше даты закупки", path: ["dueDate"] })
  .refine((d) => d.type === "receipt" || d.lines.every((l) => !l.expiresAt), { error: "Срок годности — только для закупки", path: ["lines"] })
  .superRefine((d, c) => {
    d.lines.forEach((l, i) => {
      if (!l.loss?.value) return;
      const path = ["lines", i, "loss"];
      if (d.type !== "receipt") c.addIssue({ code: "custom", message: "Потери — только для закупки", path });
      else if (l.loss.mode === "percent" && l.loss.value >= 100) c.addIssue({ code: "custom", message: "Потери меньше 100%", path });
      else if (l.loss.mode === "qty" && l.loss.value >= l.qty) c.addIssue({ code: "custom", message: "Потери меньше количества", path });
    });
  });
export type StockDocumentInput = z.output<typeof stockDocumentSchema>;

/** Срок оплаты закупки поставщика (перенос по договорённости). */
export const receiptDueDateSchema = z.strictObject({ dueDate: isoDate });

export const stockDocumentsQuery = pageQuery.extend({
  warehouseId: z.uuid().optional(),
  supplierId: z.uuid().optional(),
  /** unpaid — есть остаток к оплате, overdue — просрочено */
  payment: z.enum(["unpaid", "overdue"]).optional(),
  type: z.enum(["receipt", "writeoff", "consumption", "inventory_adjustment", "return_to_supplier", "transfer_out", "transfer_in", "production"]).optional(),
  from: isoDate.optional(),
  to: isoDate.optional(),
});

// ───────────── Аналитика ─────────────

/** Период аналитики: даты включительно, в часовом поясе заведения, не больше года. */
export const periodQuery = z
  .object({ from: isoDate, to: isoDate })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 366 * 86_400_000, { error: "Период не больше года", path: ["to"] });

// ───────── Расширенные отчёты (addon.analytics_pro, ТЗ 17 §5) ─────────

export const ABC_BY = ["revenue", "margin", "qty", "products", "customers"] as const;
export type AbcBy = (typeof ABC_BY)[number];
/** ABC: класс A — позиции, дающие первые `a`% показателя, B — до `b`%, остальное — C (по умолчанию 80/95, ТЗ 17 §5) */
export const abcQuery = z
  .object({
    from: isoDate, to: isoDate,
    by: z.enum(ABC_BY).default("revenue"),
    a: z.coerce.number().int().min(50).max(95).default(80),
    b: z.coerce.number().int().min(60).max(99).default(95),
  })
  .refine((q) => q.to >= q.from, { error: "Конец периода раньше начала", path: ["to"] })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= 366 * 86_400_000, { error: "Период не больше года", path: ["to"] })
  .refine((q) => q.b > q.a, { error: "Порог B больше порога A", path: ["b"] });

// ───────── Сброс отчётов: учёт с чистого листа ─────────

/**
 * Сброс отчётов: с даты `date` отчёты (выручка, финансы, блюда, …) считаются заново. Ничего не удаляется — заказы, оплаты,
 * смены и документы остаются в базе; отчёты за прошлое — в архиве сброса. Подтверждается паролем владельца.
 */
export const reportResetPreviewQuery = z.object({ date: isoDate });
export const reportResetSchema = z.strictObject({
  date: isoDate,
  password: z.string().min(1, { error: "Введите пароль" }).max(128),
  comment,
});
export const reportResetCancelSchema = z.strictObject({ password: z.string().min(1, { error: "Введите пароль" }).max(128) });
